import {Injectable} from "@angular/core";
import {BehaviorSubject, combineLatest, Observable, of, switchMap, tap} from "rxjs";
import {
    DocType,
    DocummentAuditStatus,
    DocUserField, InternalDocummentID,
    NotificationType, SMMDocumentAuditUpdate,
    SMMDocumment,
    SMMDocummentAudit,
    SMMLegalDocument, SMMLegalDocumentTriggerDTO
} from "./document-types";
import {ErrorHandlingService} from "../../core/services/error.handling.service";
import {UrlService} from "../../core/services/url.service";
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {UrlEnum} from "../../core/types/url-enum";
import {ObservableErrHandler} from "../../core/types/err.handling.types";
import {BasicInfoFieldNames} from "./basic-info/basic.patient.info.types";
import {PermissionsService} from "../../core/services/permissions.service";
import {SterilizedInstrument} from "../sterilization/sterilization.types";
import {docCSS} from "./base-behaviour/base.behaviour.doc.types";
import {UploadedFile} from "../patient/multimedia/file.upload.types";

@Injectable({providedIn: 'root'})
export class DocummentsService {
    private _busy: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private _allDocs: BehaviorSubject<SMMDocumment[]> = new BehaviorSubject([]);
    private _allDocFlows: BehaviorSubject<SMMDocummentAudit[]> = new BehaviorSubject([]);
    private _allUserDocFields: BehaviorSubject<DocUserField[]> = new BehaviorSubject([]);
    private _availableSMMLegalDocFlows: BehaviorSubject<SMMLegalDocument[]> = new BehaviorSubject(null);
    private userID = "";
    private patientKey = "";

    private lastUSerKeyForDocFields: string = null;
    private lastDocIDForDocFields: string = null;



    /**
     * Constructor
     */
    constructor(
        private _permissionService: PermissionsService,
        private _errHandlService: ErrorHandlingService,
        private _urlService: UrlService,
        private _httpClient: HttpClient,
    ) {
    }

    get allAvailableSMMDocFlows$(){
        if(!this._availableSMMLegalDocFlows.value ){
            // if the subscription comes before any data was fetched, then bring data from BE
            this.getAvailableLegalDocFlows();
        }
        return this._availableSMMLegalDocFlows.asObservable();
    }

    get allDocumments$(){
        return this._allDocs.asObservable();
    }

    get availableDocFlows$(){
        return this._allDocFlows.asObservable();
    }

    get availableUserDocFields$(){
        return this._allUserDocFields.asObservable();
    }

    get busy$(){
        return this._busy.asObservable();
    }

    getAvailableLegalDocFlows():void {
        this._busy.next(true);

        const obs = this._httpClient.get<SMMLegalDocument[]>(
            this._urlService.getUrl(UrlEnum.private_legal_smm_docs_get_all)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = obs.pipe(tap({
                next: (SMMflows) => {
                    this._availableSMMLegalDocFlows.next(SMMflows);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }


    createNewDocummentFlowObservable(newDocFlows: SMMLegalDocumentTriggerDTO[]): Observable<any> {
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.post<string>(
            this._urlService.getUrl(UrlEnum.private_docs_flows_trigger_bulk),
            JSON.stringify(newDocFlows),
            {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            success_title: "S-a facut",
            success_msg: "Documentele au fost create cu succes"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: result => {
                    this._busy.next(false);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        return errWrapper.observable;
    }


    createNewDocummentFlow(newDocFlows: SMMLegalDocumentTriggerDTO[]): void {
        this._busy.next(true);

        const createObservable = this.createNewDocummentFlowObservable(newDocFlows);
        const refreshObservable = this.getBEDataObservables(newDocFlows[0].userId);

        const obs = createObservable.pipe(
            switchMap(() => refreshObservable)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            success_title: "S-a facut",
            success_msg: "Documentele au fost create cu succes"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: docFlowID => {
                    this._busy.next(false);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    cancelDocFlow(docFlowId:string):void {
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.put<DocUserField[]>(
            this._urlService.getUrl(UrlEnum.private_docs_flows_cancel)
                .replace("{id}", docFlowId),
            null,
            {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error",
            success_title: "S-a facut",
            success_msg: "Fluxul de semnare a documentul a fost anulat cu succes"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: resultedFields => {
                    //mark doc flow as canceled
                    const docFlow: SMMDocummentAudit = this._allDocFlows.value.find(flow => flow.id === docFlowId);
                    if(docFlow){
                        docFlow.status = DocummentAuditStatus.CANCELED;
                        this._allDocFlows.next(this._allDocFlows.value);
                    }
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    getBEDataObservables(userId:string):Observable<any> {
        const docsObs = this._httpClient.get<SMMDocumment[]>(
            this._urlService.getUrl(UrlEnum.private_docs_get_all)
                .replace("{userId}", userId)
        );


        const flowsObs = this._httpClient.get<SMMDocummentAudit[]>(
            this._urlService.getUrl(UrlEnum.private_docs_flows_get_all)
                .replace("{userId}", userId)
        );

        const obs = combineLatest([docsObs, flowsObs]);

        return obs.pipe(tap({
                next: ([documents, flows]) => {
                    const docs:SMMDocumment[] = documents.concat([]);
                    docs.forEach((doc: SMMDocumment) => {
                        doc.type = DocType.DOC;
                    })

                    this.insertBasicInfoFlowAndDocs(flows, docs);

                    this._allDocs.next(docs);
                    this._allDocFlows.next(flows);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
    }

    getData(userId:string):void {
        if(this.userID == userId){
            //we allready got docs for this user
            return;
        }

        this._busy.next(true);
        const obs = this.getBEDataObservables(userId);

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable.subscribe();
    }

    private insertBasicInfoFlowAndDocs(flows:SMMDocummentAudit[], docs:SMMDocumment[]){
        if(!this._permissionService.isVisitor()){
            return;
        }

        if(flows?.length > 0) {
            // there are docs... we need to add UI only Basic Data capturing info flow.
            // inject basic Data flow on list
            flows.splice(0, 0, {
                id: "BASIC_INFO_FLOW",
                status: DocummentAuditStatus.USER_REVIEW,
                clinicUserReviewCompletionTime: [],
                notificationType: NotificationType.MAIL,
                userKey: flows[0].userKey,
                userWarningId: "BASIC_INFO_DOC",
                notifyUserOnCompletion: false,
                userCompletionTime: [],
                userNotificationCompletionTime: [],
                createdBy: '',
                createdOn: '',
                updatedBy: '',
                updatedOn: '',
                createdOnStr: '',
                updatedOnStr: ''
            });
        }

        if(docs?.length > 0) {
            // there are docs... we need to add UI only Basic Data capturing info flow.
            // inject basic Data flow on list
            docs.splice(0, 0, {
                id: "BASIC_INFO_DOC",
                userWarningAuditStatus: DocummentAuditStatus.USER_REVIEW,
                userKey: docs[0].userKey,
                type: DocType.PDF,
                legalDocumentInternalId: InternalDocummentID.BASIC_INF,
                legalDocumentName: "Informatii de baza",
                signature: "",
                userId: docs[0].userId,
                userWarningAuditId: "BASIC_INFO_FLOW",
                createdBy: '',
                createdOn: '',
                updatedBy: '',
                updatedOn: '',
                createdOnStr: '',
                updatedOnStr: ''
            });
        }
    }



    getDocDataByUserKey(userKey:string, status: DocummentAuditStatus):void {

        this._busy.next(true);

        const docsObs = this._httpClient.get<SMMDocumment[]>(
            this._urlService.getUrl(UrlEnum.private_doc_get_by_userKey_and_status)
                .replace("{userKey}", userKey)
                .replace("{status}", status)
        );


        const flowsObs = this._httpClient.get<SMMDocummentAudit[]>(
            this._urlService.getUrl(UrlEnum.private_doc_flow_get_by_userKey_and_status)
                .replace("{userKey}", userKey)
                .replace("{status}", status)
        );

        const fieldsObs = this._httpClient.get<DocUserField[]>(
            this._urlService.getUrl(UrlEnum.private_all_user_fields_by_patient_key)
                .replace("{usrKey}", userKey)
        );

        const obs = combineLatest([docsObs, flowsObs, fieldsObs]);

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = obs.pipe(tap({
                next: ([documents, flows, fields]) => {
                    const docs:SMMDocumment[] = documents.concat([]);
                    docs.forEach((doc: SMMDocumment) => {
                        doc.type = DocType.DOC;
                    })

                    this.insertBasicInfoFlowAndDocs(flows, docs);

                    this._allDocs.next(docs);
                    this._allDocFlows.next(flows);
                    this.updateUserInputFieldsNow(fields);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));

        errWrapper.observable.subscribe();
    }

    getAllDocsFromBE(userId:string):void {
        if(this.userID == userId){
            //we allready got docs for this user
            return;
        }

        this._busy.next(true);
        const obs = this._httpClient.get<SMMDocumment[]>(
            this._urlService.getUrl(UrlEnum.private_docs_get_all)
            .replace("{userId}", userId)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: value => {
                    const docs:SMMDocumment[] = value.concat([]);
                    docs.forEach((doc: SMMDocumment) => {
                        doc.type = DocType.DOC;
                    })

                    this._allDocs.next(docs);

                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));

        errWrapper.observable.subscribe();
    }

    getDocFromBE(DOCid:string):Observable<SMMDocumment> {
        this._busy.next(true);
        const obs = this._httpClient.get<SMMDocumment>(
            this._urlService.getUrl(UrlEnum.private_docs_get)
                .replace("{id}", DOCid)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        return errWrapper.observable;
    }

    getFlowFromBE(flowID:string):Observable<SMMDocummentAudit> {
        this._busy.next(true);
        const obs = this._httpClient.get<SMMDocummentAudit>(
            this._urlService.getUrl(UrlEnum.private_docs_flows_get)
                .replace("{id}", flowID)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        return errWrapper.observable;
    }

    getAllDocFlowsFromBE(userId:string):void {
        if(this.userID == userId){
            //we allready got docs for this user
            return;
        }

        this._busy.next(true);
        const obs = this._httpClient.get<SMMDocummentAudit[]>(
            this._urlService.getUrl(UrlEnum.private_docs_flows_get_all)
                .replace("{userId}", userId)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: value => {
                    this._allDocFlows.next(value);
                    this.userID = userId
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));

        errWrapper.observable.subscribe();
    }

    gatAllUserInputFields(patientKey:string):void {
        if(this.lastUSerKeyForDocFields == patientKey){
            //we allready got docs for this user
            return;
        }

        this._busy.next(true);
        const obs = this.getAllUserInputFieldsObs(patientKey);

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: value => {
                    this.updateFieldsData(value);
                    this.patientKey = patientKey;
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));

        errWrapper.observable.subscribe();
    }

    updateUserInputFieldsNow(fields:DocUserField[]) {
        this.updateFieldsData(fields);
    }

    getAllUserInputFieldsObs(patientKey:string):Observable<DocUserField[]> {
        if(this.lastUSerKeyForDocFields == patientKey && this._allUserDocFields.value?.length > 0){
            //we allready got doc fields for this user key
            return of(this._allUserDocFields.value);
        }

        const obs = this._httpClient.get<DocUserField[]>(
            this._urlService.getUrl(UrlEnum.private_all_user_fields_by_patient_key)
                .replace("{usrKey}", patientKey)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);

        errWrapper.observable.pipe(tap(value => {
            this.updateUserInputFieldsNow(value);
            this.lastDocIDForDocFields = patientKey;
        }));

        return errWrapper.observable;
    }

    getAllUserInputFieldsByDocIDObs(docID:string):Observable<DocUserField[]> {
        if(this.lastDocIDForDocFields == docID && this._allUserDocFields.value?.length > 0){
            //we allready got doc fields for this user key
            return of(this._allUserDocFields.value);
        }

        const obs = this._httpClient.get<DocUserField[]>(
            this._urlService.getUrl(UrlEnum.private_all_user_fields_by_docID)
                .replace("{userWarningId}", docID)
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);

        errWrapper.observable.pipe(tap(value => {
            this.updateUserInputFieldsNow(value);
            this.lastDocIDForDocFields = docID;
        }));

        return errWrapper.observable;
    }

    public syncFields(fields:DocUserField[], docID:string):void {

        //sanitize nulls and undefined
        fields.forEach(value => {
            value.value = !value.value? "":value.value;
        })

        const fieldsToUpdate: DocUserField[]= [];
        const fieldsToCreate: DocUserField[]= [];

        fields.forEach(field => {
            const foundField = this._allUserDocFields.value.find(
                fieldX => fieldX.name === field.name &&
                fieldX.userWarningId === field.userWarningId
            );

            if(foundField && foundField?.id){
                field.id = foundField.id;
                fieldsToUpdate.push(field);
            }else{
                // no id because we create
                fieldsToCreate.push(field);
            }
        })

        if(fieldsToUpdate.length > 0){
            this.updateUserDocFields(fieldsToUpdate, docID);
        }

        if(fieldsToCreate.length > 0){
            this.createUserDocFields(fieldsToCreate, docID);
        }
    }

    public updateUserDocFields(fields:DocUserField[], docID:string):void {
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.put<DocUserField[]>(
            this._urlService.getUrl(UrlEnum.private_update_user_field_bulk)
                .replace("{userWarningId}", docID),
            JSON.stringify(fields),
            {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: resultedFields => {
                    //remove existing fields from current inMem array to avoid duplicates
                    const removedArr: DocUserField[] = this._allUserDocFields.value.filter(
                        field => resultedFields.findIndex(fieldS => fieldS.id === field.id) < 0
                    )

                    //add result
                    const newArr = removedArr.concat(resultedFields);
                    this.updateFieldsData(newArr);
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    public createUserDocFields(fields:DocUserField[], docID:string):void {
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.post<DocUserField[]>(
            this._urlService.getUrl(UrlEnum.private_create_user_field_bulk)
                .replace("{userWarningId}", docID),
            JSON.stringify(fields),
            {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: fields => {
                    this.updateFieldsData(this._allUserDocFields.value.concat(fields));
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    updateDocFlowStatus(flowID: string, status: DocummentAuditStatus) {
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.put<string>(
            this._urlService.getUrl(UrlEnum.private_update_doc_flow_status)
                .replace("{nextStatus}", status)
                .replace("{id}", flowID),
            null,
            {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: result => {
                    //remove existing fields from current inMem array to avoid duplicates
                    //add result
                    const index = this._allDocFlows.value.findIndex(flowDoc => flowDoc.id === flowID);
                    if(index > -1) {
                        this._allDocFlows.value[index].status = status;
                        this._allDocFlows.next(this._allDocFlows.value);
                    }
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    updateDocFlowNotification(update:SMMDocumentAuditUpdate, flowID:string){
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');

        const obs = this._httpClient.put<string>(
            this._urlService.getUrl(UrlEnum.private_update_doc_notification)
                .replace("{id}", flowID),
            update,
            {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: result => {
                    //remove existing fields from current inMem array to avoid duplicates
                    //add result
                    const index = this._allDocFlows.value.findIndex(flowDoc => flowDoc.id === flowID);
                    if(index > -1) {
                        this._allDocFlows.value[index].notificationType = update.notificationType;
                        this._allDocFlows.value[index].notifyUserOnCompletion = update.notifyUserOnCompletion;
                        this._allDocFlows.next(this._allDocFlows.value);
                    }
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }


    updateSignature(documentID: string, signatureVal: string, callback?: () => void) {
        this._busy.next(true);

        const headers = new HttpHeaders().set('Content-Type', 'text/html; charset=utf-8');

        const obs = this._httpClient.put<string>(
            this._urlService.getUrl(UrlEnum.private_doc_update_signature)
                .replace("{id}", documentID),
            signatureVal,
            {headers: headers}
        );

        const errWrapper: ObservableErrHandler = {
            observable: obs,
            err_title: "Error"
        };

        this._errHandlService.watchThis(errWrapper);
        errWrapper.observable = errWrapper.observable.pipe(tap({
                next: result => {
                    callback();
                },
                error: err => {
                    this._busy.next(false);
                },
                complete: () => {
                    this._busy.next(false);
                }
            }
        ));
        errWrapper.observable.subscribe();
    }

    private updateFieldsData(value:DocUserField[]) {
        this._allUserDocFields.next(value);
        this.updateStatusOfBasicFlowAndDoc();
    }

    public updateStatusOfBasicFlowAndDoc(){
        //we look into field data for fiedls indicating status of basic flow, and we update status if we find any
        this._allUserDocFields.value.forEach(field => {
            if(field.name == BasicInfoFieldNames.basic_flowStatusField){
                if(field.value){
                    const status: DocummentAuditStatus = DocummentAuditStatus[field.value];
                    //searching basic info flow
                    const foundFlow: SMMDocummentAudit = this._allDocFlows.value.find(flow => flow.id == "BASIC_INFO_FLOW");
                    if(foundFlow){
                        foundFlow.status = status;
                        this._allDocFlows.next(this._allDocFlows.value);
                    }

                    //searching basic info doc
                    const foundDoc: SMMDocumment = this._allDocs.value.find(doc => doc.id == "BASIC_INFO_DOC");
                    if(foundDoc){
                        foundDoc.userWarningAuditStatus = status;
                        this._allDocs.next(this._allDocs.value);
                    }
                }
            }
        })
    }


    printDocumment(labelHtmlData:string) {
        // Get the div content

        // Open a new window
        const newWindow = window.open();
        if (!newWindow) {
            console.error('Failed to open new window!');
            return;
        }

        // Build HTML for the new window
        newWindow.document.write(`
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Documment</title>
                <style>
                   ${docCSS}
                </style>
            </head>
            <body>
                ${labelHtmlData}
            </body>

            </html>
        `);

        // Ensure the document is fully loaded
        newWindow.document.close();

        // Print the new window
        newWindow.print();

        newWindow.close();

        setTimeout(() => {
            newWindow.close();
        }, 1000);

    }

    printDiv(divId: string) {
        const divContents = document.getElementById(divId)?.innerHTML;
        const printFrame = document.createElement('iframe');

        printFrame.style.position = 'absolute';
        printFrame.style.width = '0px';
        printFrame.style.height = '0px';
        printFrame.style.border = 'none';

        document.body.appendChild(printFrame);

        const frameDoc = printFrame.contentWindow?.document;
        if (frameDoc && divContents) {
            frameDoc.open();
            frameDoc.write(`
            <!DOCTYPE html>
            <html lang="en">
            <head>
                <meta charset="UTF-8">
                <meta name="viewport" content="width=device-width, initial-scale=1.0">
                <title>Documment</title>
                <style>
                   ${docCSS}
                </style>
            </head>
            <body>
                ${divContents}
            </body>
            </html>
        `);
            frameDoc.close();
            printFrame.contentWindow?.focus();
            printFrame.contentWindow?.print();
            document.body.removeChild(printFrame);
        }
    }

    printDivWithCss(divId: string) {
        const inner=document.getElementById(divId).innerHTML;
        const style = window.getComputedStyle(document.getElementById(divId)).all;
        const html="<html><head>"+style+"</head><body>"+inner+"</body></html>";

        const printFrame = document.createElement('iframe');

        printFrame.style.position = 'absolute';
        printFrame.style.width = '0px';
        printFrame.style.height = '0px';
        printFrame.style.border = 'none';

        document.body.appendChild(printFrame);

        const frameDoc = printFrame.contentWindow?.document;
        if (frameDoc && html) {
            frameDoc.open();
            frameDoc.write(html);
            frameDoc.close();
            printFrame.contentWindow?.focus();
            printFrame.contentWindow?.print();
            document.body.removeChild(printFrame);
        }
    }


}
